import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MapPin, Heart, LayoutDashboard, Shield, LogOut, Menu } from "lucide-react";
import { Logo } from "@/components/Logo";

export function Navbar() {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const transparent = isHome && !scrolled;

  const openAuth = (m) => { setMode(m); setAuthOpen(true); };

  const links = [
    { to: "/explore", label: "Explore" },
    { to: "/events", label: "Events" },
    { to: "/deals", label: "Deals" },
    { to: "/map", label: "Map" },
  ];

  return (
    <>
      <header className={`${isHome ? "fixed" : "sticky"} inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent ? "bg-transparent" : "glass-header border-b border-slate-200/60"
      }`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center" data-testid="logo-home">
            <Logo size="text-3xl" light={transparent} showIcon={!transparent} />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${transparent ? "text-white/90 hover:text-white drop-shadow" : "text-slate-600 hover:text-slate-900"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                {(user.role === "customer" || user.role === "business_owner") && (
                  <Link to="/favorites" className="hidden sm:block">
                    <Button variant="ghost" size="icon" className={`rounded-full ${transparent ? "text-white hover:bg-white/15 hover:text-white" : ""}`} data-testid="nav-favorites">
                      <Heart className="w-5 h-5" strokeWidth={1.5} />
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <Avatar className="w-9 h-9 border-2 border-orange-100">
                        <AvatarFallback className="bg-orange-500 text-white text-sm font-semibold">
                          {user.name?.[0]?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-2xl w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-sm truncate">{user.name}</p>
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {user.role === "business_owner" && (
                      <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Business Dashboard
                      </DropdownMenuItem>
                    )}
                    {user.role === "customer" && (
                      <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-claim">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Claim a business
                      </DropdownMenuItem>
                    )}
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/favorites")}>
                      <Heart className="w-4 h-4 mr-2" /> Saved
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => openAuth("login")} data-testid="nav-login"
                  className={`rounded-full hidden sm:inline-flex ${transparent ? "text-white hover:bg-white/15 hover:text-white" : ""}`}>Sign in</Button>
                <Button onClick={() => openAuth("register")} data-testid="nav-signup"
                  className="rounded-full bg-orange-500 hover:bg-orange-600">Get started</Button>
              </>
            )}
            <button className={`md:hidden p-2 ${transparent ? "text-white" : "text-slate-900"}`} onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200/60 px-5 py-3 space-y-1 bg-white/95">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                className="block py-2 text-slate-700 font-medium">{l.label}</Link>
            ))}
          </div>
        )}
      </header>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={mode} />
    </>
  );
}
